import { useForm } from 'app/hooks/form';
import { env } from 'app/lib/env';
import { DEFAULT_COUNTRY } from 'app/lib/language';
import { storage, StorageKey } from 'app/lib/storage';
import type { CreateAddressDTO, TemporaryAddressDTO } from 'app/network/address-mutations';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

export type ContactInformationFormValues = Omit<CreateAddressDTO, 'latitude' | 'longitude'>;

export function useContactInformationForm() {
  const { t } = useTranslation('common');

  const schema: yup.ObjectSchema<ContactInformationFormValues> = yup.object({
    administrativeArea: yup.string().min(2).max(3).trim().nullable().defined(),
    city: yup.string().max(255).trim().nullable().defined(),
    country: yup.string().length(2).trim().required(),
    postalCode: yup
      .string()
      .trim()
      .min(4)
      .max(10)
      .required(t('postalCodeRequiredError', { ns: 'common' })),
    street: yup.string().max(255).trim().nullable().defined(),
    unit: yup.string().max(255).trim().nullable().defined(),
    radiusCenterLatitude: yup.number().nullable().defined(),
    radiusCenterLongitude: yup.number().nullable().defined(),
    name: yup.string().nullable().defined(),
    radius: yup.number().nullable().defined(),
    icon: yup.number().nullable().defined(),
  });

  const { control, formState, handleSubmit, reset } = useForm<ContactInformationFormValues>({
    defaultValues: {
      administrativeArea: null,
      city: null,
      country: DEFAULT_COUNTRY,
      street: null,
      unit: null,
      radiusCenterLatitude: null,
      radiusCenterLongitude: null,
      name: null,
      radius: null,
      icon: null,
      postalCode: env.APP_ENV === 'local' ? (env.DEFAULT_POSTAL_CODE?.toUpperCase() ?? '') : '',
    },
    schema,
    validationMode: 'onChange',
  });

  useEffect(() => {
    const loadStoredAddress = async () => {
      const temporaryAddress = await storage.readString(StorageKey.TemporaryAddressUsed);
      const parsedAddress = temporaryAddress ? (JSON.parse(temporaryAddress) as TemporaryAddressDTO) : null;
      if (parsedAddress) {
        reset({
          administrativeArea: null,
          city: null,
          country: parsedAddress.country || DEFAULT_COUNTRY,
          street: null,
          unit: null,
          radiusCenterLatitude: null,
          radiusCenterLongitude: null,
          name: null,
          radius: null,
          icon: null,
          postalCode: (parsedAddress.postalCode || '').toUpperCase(),
        });
      }
    };
    void loadStoredAddress();
  }, [reset]);

  return { control, formState, handleSubmit, reset };
}
